<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <div class="admin-layout">
    <!-- 左侧固定菜单 -->
    <el-aside v-if="menuVisible" class="admin-sidebar" width="240px">
      <div class="logo">
        <h2>点餐管理系统</h2>
      </div>
      <el-menu
        :default-active="activeMenu"
        class="admin-menu"
        :router="true"
        :collapse="false"
        background-color="#304156"
        text-color="#bfcbd9"
        active-text-color="#409EFF"
      >
        <div v-for="menu in showMenus" :key="menu.key">
          <el-menu-item :index="menu.path">
            <el-icon><component :is="menu.icon" /></el-icon>
            <span>{{menu.title}}</span>
          </el-menu-item>
        </div>
      </el-menu>
      <div class="icp-footer"><a href="https://beian.miit.gov.cn/" target="_blank">辽ICP备2026007340号-1</a></div>
    </el-aside>

    <!-- 右侧主内容区 -->
    <el-container class="admin-main">
      <el-header class="admin-header">
        <div class="header-left">
          <el-icon @click="toggleSidebar" class="collapse-btn"><Fold /></el-icon>
          <div class="shop-info">
            <span class="shop-name">{{ tenantName }}</span>
          </div>
          <span class="page-title">{{ currentRouteMeta?.title || '欢迎' }}</span>
          <el-icon class="refresh-btn" @click="handleRefresh"><Refresh /></el-icon>
        </div>
        <div class="header-right">
          <el-dropdown @command="handleCommand">
            <span class="user-info">
              <el-icon :size="24"><Avatar /></el-icon>
              <span class="username">{{ userName }}</span>
              <el-icon><ArrowDown /></el-icon>
            </span>
            <template #dropdown>
              <el-dropdown-menu>
                <el-dropdown-item command="profile">个人中心</el-dropdown-item>
                <el-dropdown-item command="logout" divided>退出登录</el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
        </div>
      </el-header>
      <el-main class="admin-content">
        <router-view v-slot="{ Component }">
          <transition name="fade" mode="out-in">
            <component :is="Component" :key="routeKey" />
          </transition>
        </router-view>
      </el-main>
    </el-container>
  </div>
</template>

<script setup>
import { computed, ref, onMounted, onUnmounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ElMessageBox, ElMessage } from 'element-plus'
import {  Fold, ArrowDown, Refresh, Avatar} from '@element-plus/icons-vue'
import { useUserStore } from '@/stores/admin/userStore'
import sseService from '@/utils/sseService'
import speakService from '@/utils/speakService'

const route = useRoute()
const router = useRouter()
const userStore = useUserStore()

const activeMenu = computed(() => route.path)
const currentRouteMeta = computed(() => route.meta)
const userName = sessionStorage.getItem('userName')
const tenantName = sessionStorage.getItem('tenantName')

const menuVisible = ref(true)

const menuList = [
  {key: 'menu', title: '菜单管理', path: '/admin/menu', icon: 'Food'},
  {key: 'table', title: '桌台管理', path: '/admin/table', icon: 'OfficeBuilding'},
  {key: 'serve', title: '上菜管理', path: '/admin/serve', icon: 'Dish'},
  {key: 'statistics', title: '统计', path: '/admin/statistics', icon: 'Histogram'},
]

const showMenus = computed( () => {
  return menuList.filter(menu => sessionStorage.getItem('permission')?.split(',')?.includes(menu.key))
})

// 刷新 key
const routeKey = ref(0)

// 刷新方法
const handleRefresh = () => {
  routeKey.value += 1
}

const toggleSidebar = () => {
  // 预留折叠功能，可后续实现
  menuVisible.value = !menuVisible.value
}

const handleCommand = (command) => {
  if (command === 'logout') {
    ElMessageBox.confirm('确认退出登录吗？', '提示', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning'
    }).then( async () => {
      await userStore.logout()
      router.push('/login')
      ElMessage.success("退出登录成功")
    }).catch(() => {})
  } else if (command === 'profile') {
    // 跳转到个人中心（暂未实现）
    ElMessage.info('个人中心功能开发中')
  }
}

const sseCallback = async (e) => {
  if(e.data){
    speakService.speak(e.data)
  }
}

onUnmounted(() => {
  sseService.closeSSE()
})

// 加载数据
onMounted(async () => {
  sseService.connectSSE()
  sseService.on('checkout', (e) => {
    sseCallback(e)
  })
})
</script>

<style scoped>
.admin-layout {
  height: 100vh;
  display: flex;
}
.admin-sidebar {
  background-color: #304156;
  color: #fff;
  overflow-y: auto;
  box-shadow: 2px 0 6px rgba(0,0,0,0.05);
  display: flex;
  flex-direction: column;
  height: 100vh;
}
.el-menu {
  flex: 1; /* 菜单自动撑开，底部留出空间 */
}
.icp-footer {
  padding: 16px;
  text-align: center;
  margin-top: auto; /* 自动推到底部 */
}

.icp-footer a{
  font-size: 12px;
  color: #BFCBD9;
}

.logo {
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #2b3a4a;
}
.logo h2 {
  margin: 0;
  font-size: 18px;
  color: #fff;
  font-weight: 500;
}
.admin-menu {
  border-right: none;
  height: calc(100vh - 60px);
}
.admin-main {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0; /* 防止flex溢出 */
}
.admin-header {
  background-color: #fff;
  border-bottom: 1px solid #e6e6e6;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 24px;
  height: 60px;
}
.header-left {
  display: flex;
  align-items: center;
  gap: 20px;
  flex-wrap: wrap; /* 允许换行，避免内容挤压 */
}
.collapse-btn {
  font-size: 20px;
  cursor: pointer;
  color: #606266;
}
.page-title {
  font-size: 16px;
  font-weight: 500;
  color: #303133;
}
.header-right {
  display: flex;
  align-items: center;
}
.user-info {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  transition: background 0.2s;
}
.user-info:hover {
  background: #f5f7fa;
}
.username {
  font-size: 14px;
  color: #333;
}
.admin-content {
  background-color: #f0f2f5;
  padding: 24px;
  overflow-y: auto;
}
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.refresh-btn {
  font-size: 18px;
  cursor: pointer;
  color: #606266;
  transition: color 0.2s;
}

.refresh-btn:hover {
  color: #409EFF;
}

.shop-info {
  display: flex;
  align-items: center;
  gap: 6px;
  padding-right: 16px;
  border-right: 1px solid #e6e6e6;  /* 与右侧内容分隔 */
  line-height: 1;
}

.shop-name {
  font-size: 16px;
  font-weight: 500;
}

@media (max-width: 768px) {
  .shop-name {
    font-size: 14px;
  }
  .header-left {
    gap: 12px;
  }
  .shop-info {
    padding-right: 10px;
  }
}
</style>